import React from 'react';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import TagFilter from '../TagFilter';
import { getLogoForStyle } from '../../utils/styleLogos';

const DAY_MS = 24 * 60 * 60 * 1000;

// Short "expires in…" label for an event style so users spend soonest-expiring ones first.
function formatEventExpiry(endsAtMs) {
  if (!endsAtMs) return 'Event style';
  const remaining = endsAtMs - Date.now();
  if (remaining <= 0) return 'Ending';
  const days = Math.ceil(remaining / DAY_MS);
  if (days <= 1) return 'Ends today';
  if (days <= 7) return `Ends in ${days}d`;
  return `Ends in ${Math.ceil(days / 7)}w`;
}

// Presentational style picker extracted from CreatePage's `style-selection-section`.
// Fully controlled: the parent owns the sorted list, selection, tag-filter state and
// pagination. Keeps CreatePage's exact class names so CreatePage.css carries over
// (dark mode, hover, selected states). Reused by the referral/event map builders.
function StyleSelectionSection({
  title = 'Choose style',
  isLoading = false,
  loadingText = 'Loading styles...',
  emptyText = 'No styles available',
  isEmpty = false,
  pageItems = [],
  selectedStyleName = null,
  onSelectStyle,
  onPreloadStyle,
  tagFilter = null,
  page = 0,
  totalPages = 1,
  onPageChange,
  eventStyles = null, // [{ group, endsAtMs }] — pinned "your event styles" row (optional)
  getStyleBadge = null, // (styleGroup) => string | null — e.g. "Free" on the Zwift Map style
}) {
  return (
    <div className="style-selection-section">
      <h2 className="section-title">{title}</h2>

      {!isLoading && Array.isArray(eventStyles) && eventStyles.length > 0 && (
        <div className="event-styles-row">
          <div className="event-styles-row-label">✨ Your event styles</div>
          <div className="event-styles-row-cards">
            {eventStyles.map(({ group, endsAtMs }) => {
              const logoSrc = getLogoForStyle(group.name, group.logoUrl);
              const isSelected = selectedStyleName === group.name;
              return (
                <div
                  key={group.name}
                  className={`event-style-card ${isSelected ? 'selected' : ''}`}
                  onClick={() => onSelectStyle?.(group)}
                  onMouseEnter={() => onPreloadStyle?.(group)}
                  onTouchStart={() => onPreloadStyle?.(group)}
                >
                  <div className="event-style-logo">
                    {logoSrc ? <img src={logoSrc} alt={group.name} /> : <span>🗺️</span>}
                  </div>
                  <div className="event-style-info">
                    <span className="event-style-name">{group.name}</span>
                    <span className="event-style-expiry">{formatEventExpiry(endsAtMs)}</span>
                  </div>
                  {isSelected && <div className="selection-check">✓</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!isLoading && tagFilter && (
        <TagFilter
          availableTagCategories={tagFilter.availableTagCategories}
          activeTagCategory={tagFilter.activeTagCategory}
          activeTag={tagFilter.activeTag}
          onChangeTagCategory={tagFilter.onChangeTagCategory}
          onChangeTag={tagFilter.onChangeTag}
        />
      )}

      {isLoading ? (
        <div className="loading-state">{loadingText}</div>
      ) : (
        <>
          <div className="styles-grid-main">
            {isEmpty ? (
              <p style={{ gridColumn: '1 / -1', textAlign: 'center', color: '#6b6b6b' }}>
                {emptyText}
              </p>
            ) : (
              pageItems.map((styleGroup) => {
                const logoSrc = getLogoForStyle(styleGroup.name, styleGroup.logoUrl);
                const isSelected = selectedStyleName === styleGroup.name;
                const badge = getStyleBadge?.(styleGroup) || null;
                return (
                  <div
                    key={styleGroup.name}
                    className={`style-option ${isSelected ? 'selected' : ''}`}
                    onClick={() => onSelectStyle?.(styleGroup)}
                    onMouseEnter={() => onPreloadStyle?.(styleGroup)}
                    onTouchStart={() => onPreloadStyle?.(styleGroup)}
                  >
                    <div className="style-logo">
                      {logoSrc ? (
                        <img src={logoSrc} alt={styleGroup.name} />
                      ) : (
                        <div style={{ fontSize: '40px' }}>🗺️</div>
                      )}
                    </div>
                    <p className="style-label">{styleGroup.name}</p>
                    {badge && <span className="style-free-badge">{badge}</span>}
                    {isSelected && <div className="selection-check">✓</div>}
                  </div>
                );
              })
            )}
          </div>
          {totalPages > 1 && (
            <div className="activities-navigation">
              <button
                onClick={() => onPageChange?.(page - 1)}
                disabled={page === 0}
                className="activity-nav-btn"
              >
                <IconChevronLeft size={20} />
              </button>
              <span className="page-indicator">{page + 1} / {totalPages}</span>
              <button
                onClick={() => onPageChange?.(page + 1)}
                disabled={page >= totalPages - 1}
                className="activity-nav-btn"
              >
                <IconChevronRight size={20} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default StyleSelectionSection;
